import { useQuery } from "@apollo/client";
import { IonCol, IonGrid, IonRow } from "@ionic/react";
import equal from "deep-equal";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { createEnumParam, StringParam, useQueryParams, withDefault } from "use-query-params";

import AsyncSelect, { Option } from "@components/fields/AsyncSelect";
import Select from "@components/fields/Select";
import Form from "@components/forms/Form";
import HandleQuery from "@components/HandleQuery";
import useAsyncSearch from "@hooks/useAsyncSearch";
import useLazyQueryWithPromise from "@hooks/useLazyQueryWithPromise";
import useMyExpert from "@hooks/useMyExpert";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import {
  ExpertGlobalRoleEnum,
  GetExpertClientsDocument,
  GetExpertClientsQuery,
  GetExpertClientsQueryVariables,
  GetExpertsBasicDocument,
  GetExpertsBasicQuery,
  GetExpertsBasicQueryVariables,
  TaskAssigneeEnum,
  TaskStatusEnum
} from "@typing/Generated";

type Client = NonNullable<NonNullable<GetExpertClientsQuery["expertClients"]["edges"]>[number]>["node"];

type FormData = {
  assignee?: TaskAssigneeEnum | null;
  clientId?: string | null;
  expertId?: string | null;
  status?: TaskStatusEnum | null;
};

type Props = {
  filters: FormData;
  hideClientId?: boolean;
  onChange: (filterValues: FormData) => void;
};

const TaskFilters = ({ filters, hideClientId = false, onChange }: Props) => {
  const intl = useIntl();
  const myExpert = useMyExpert();

  const assigneeParam = createEnumParam<TaskAssigneeEnum>(Object.values(TaskAssigneeEnum));
  const statusParam = createEnumParam<TaskStatusEnum>(Object.values(TaskStatusEnum));

  const [query, setQuery] = useQueryParams({
    assignee: withDefault({ ...assigneeParam, urlName: "taskAssignee" }, filters.assignee),
    clientId: withDefault({ ...StringParam, urlName: "taskClientId" }, filters.clientId),
    clientName: { ...StringParam, urlName: "taskClientName" },
    expertId: withDefault({ ...StringParam, urlName: "taskExpertId" }, filters.expertId),
    status: withDefault({ ...statusParam, urlName: "taskStatus" }, filters.status)
  });

  const defaultValues: FormData = query;

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange"
  });

  const assignee = form.watch("assignee");
  const clientId = form.watch("clientId");
  const status = form.watch("status");
  const expertId = form.watch("expertId");

  const getExpertClientsQuery = useLazyQueryWithPromise<GetExpertClientsQuery, GetExpertClientsQueryVariables>(
    GetExpertClientsDocument
  );

  const expertClientsAsyncSearch = useAsyncSearch<Client>({
    defaultOptions: [
      { label: intl.formatMessage({ id: "dictionary.any" }), value: "" },
      { label: intl.formatMessage({ id: "dictionary.none" }), value: "none" }
    ],
    getLabel: t => t?.fullName ?? "",
    getValue: t => t?.id ?? "",
    initialOption: { label: query.clientName, value: query.clientId }
  });

  const getExpertsBasicQuery = useQuery<GetExpertsBasicQuery, GetExpertsBasicQueryVariables>(GetExpertsBasicDocument);

  useEffect(() => {
    if (!getExpertsBasicQuery.loading && getExpertsBasicQuery.data?.experts.pageInfo.hasNextPage) {
      getExpertsBasicQuery.fetchMore({
        variables: { cursor: getExpertsBasicQuery.data?.experts.pageInfo.endCursor ?? "" }
      });
    }
  }, [getExpertsBasicQuery]);

  const experts = useNodesFromConnection(getExpertsBasicQuery.data?.experts).filter(
    expert => expert.id !== myExpert?.id
  );

  useEffect(() => {
    const values = { assignee, clientId, expertId, status };
    if (!equal(values, filters)) {
      setQuery(values);
      onChange(values);
    }
  }, [assignee, clientId, expertId, filters, onChange, setQuery, status]);

  const statusOptions = useMemo(() => {
    const options = [TaskStatusEnum.ALL, TaskStatusEnum.COMPLETED, TaskStatusEnum.INCOMPLETE];

    return options.map(option => ({
      label: intl.formatMessage({
        id: `models.tasks.status.${option}`
      }),
      value: option
    }));
  }, [intl]);

  const assigneeOptions = useMemo(() => {
    const options = [TaskAssigneeEnum.ALL, TaskAssigneeEnum.ME, TaskAssigneeEnum.OTHERS];

    return options.map(option => ({
      label: intl.formatMessage({
        id: `models.tasks.assignee.${option}`
      }),
      value: option
    }));
  }, [intl]);

  const expertOptions = useMemo(() => {
    const options = experts.map(expert => ({
      label: expert.fullName,
      value: expert.id
    }));

    options.unshift({ label: intl.formatMessage({ id: "dictionary.all" }), value: "" });
    if (myExpert) {
      options.unshift({ label: intl.formatMessage({ id: "dictionary.me" }), value: myExpert.id });
    }
    return options;
  }, [experts, intl, myExpert]);

  return (
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    <Form hookForm={form} onSubmit={() => {}}>
      <HandleQuery query={getExpertsBasicQuery}>
        {_expertData => (
          <IonGrid className="ion-no-padding">
            <IonRow className="ion-align-items-center">
              <IonCol className="ion-margin-end" size={hideClientId ? "4" : undefined}>
                <Select<FormData>
                  control={form.control}
                  error={form.formState.errors.status}
                  label={intl.formatMessage({ id: "forms.task.statusLabel" })}
                  name="status"
                  options={statusOptions}
                />
              </IonCol>
              <IonCol className="ion-margin-end" size={hideClientId ? "4" : undefined}>
                <Select<FormData>
                  control={form.control}
                  error={form.formState.errors.assignee}
                  label={intl.formatMessage({ id: "forms.task.assigneeLabel" })}
                  name="assignee"
                  options={assigneeOptions}
                />
              </IonCol>
              {!hideClientId && (
                <>
                  <IonCol
                    className={
                      myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.MANAGER ? "ion-margin-end" : "ion-hide"
                    }
                  >
                    <Select<FormData>
                      control={form.control}
                      error={form.formState.errors.expertId}
                      label={intl.formatMessage({ id: "forms.task.expertLabel" })}
                      name="expertId"
                      options={expertOptions}
                    />
                  </IonCol>
                  <IonCol>
                    <AsyncSelect<FormData>
                      control={form.control}
                      defaultOptions={expertClientsAsyncSearch.defaultOptions}
                      error={form.formState.errors.clientId}
                      label={intl.formatMessage({ id: "forms.task.clientIdLabel" })}
                      name="clientId"
                      onSearch={async searchTerm =>
                        expertClientsAsyncSearch.searchOptions(
                          searchTerm,
                          getExpertClientsQuery({
                            variables: {
                              expertId:
                                expertId ??
                                (myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.MANAGER ? "" : myExpert?.id ?? ""),
                              searchTerm
                            }
                          })
                        )
                      }
                      onSelection={(option: Option) => {
                        setQuery({ ...query, ...{ clientName: option.label } });
                        expertClientsAsyncSearch.setCurrentOption(option);
                      }}
                      value={expertClientsAsyncSearch.currentOption}
                    />
                  </IonCol>
                </>
              )}
            </IonRow>
          </IonGrid>
        )}
      </HandleQuery>
    </Form>
  );
};

export default TaskFilters;
